import { useState, useEffect } from 'react';
import { signUp, confirmSignUp, signIn, resendSignUpCode } from 'aws-amplify/auth';
import { MDBInput, MDBBtn, MDBRow, MDBCol, MDBCheckbox, MDBIcon, MDBCard, MDBCardBody, MDBTabs, MDBTabsItem, MDBTabsLink, MDBTabsContent, MDBTabsPane, MDBCardTitle, MDBFooter } from 'mdb-react-ui-kit';
import { useTranslation } from "react-i18next";
import { generateClient, put } from 'aws-amplify/api';
import useDebounce from '../../utils/debounce';
import PhoneInput from "react-phone-input-2";
import LangSelect from "../LangSelect";
import { hotjar } from "react-hotjar";
import { useIndustry } from "../../utils/industryContextHook";
import Footer from "./Footer";
import { captureError } from '../../utils/sentryHelpers';
import { fetchEvent } from '../../utils/fetchEvent';



const client = generateClient();

const SignUp = ({ authRoute, setAuthRoute, isStepConfirm, setIsStepConfirm, email, password }) => {
  const { currentIndustry } = useIndustry();
  const { t, i18n } = useTranslation();
  const [newUser, setNewUser] = useState({
    email: email || '',
    password: password || '',
    passwordConfirm: '',
    firstName: '',
    lastName: '',
    middleName: '',
    phone: '',
    confirmationCode: ''
  });
  const [emailForConfirmation, setEmailForConfirmation] = useState(email || null);
  const [emailAlreadyExists, setEmailAlreadyExists] = useState(false);
  const [error, setError] = useState(null);
  const [resendInvitationStatus, setResendInvitationStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const loadEvent = async (eventId, ticketType) => {
    const event = await fetchEvent(eventId);
    if (!event || !event.name) return;
    const nameEntry = event.name.find((n) => n.language === i18n.language) || event.name[0];
    if (nameEntry && nameEntry.text) {
      setBookingProcessData({
        productName: nameEntry.text,
        productDescription: "Ticket",
      });
    }
  };

  const handleChangeInput = (e) => {
    setNewUser({
      ...newUser,
      [e.target.id]: e.target.value
    });
  };
  const [isEmailValid, setIsEmailValid] = useState(false);
  const debouncedEmail = useDebounce(newUser.email, 1000); // 1 Sekunde Verzögerung

  // E-Mail-Validierungsfunktion
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  useEffect(() => {
    // E-Mail validieren, wenn debouncedEmail sich ändert
    if (debouncedEmail.includes('@')) {
      setIsEmailValid(validateEmail(debouncedEmail));
    } else {
      setIsEmailValid(false);
    }
  }, [debouncedEmail]);

  const checkEmailAvailable = async () => {
    try {
      const restOperation = put({
        apiName: 'CPRESTAPI',
        path: '/user/checkemailavailable',
        options: {

          body: {
            email: newUser.email
          },
        }
      });
      const responseCode = await (await restOperation.response).statusCode;
      if (responseCode === 200) {
        setEmailAlreadyExists(false);
      } else if (responseCode === 204) {
        setEmailAlreadyExists(true);
      }

    } catch (error) {
      console.error('error', error);
      captureError(error, {
        feature: 'authentication',
        operation: 'check-email-available',
        context: { email: newUser.email },
        tags: { component: 'SignUp' }
      });
    }
  };

  useEffect(() => {
    if (isEmailValid) {
      checkEmailAvailable();
    }
  }, [isEmailValid, debouncedEmail]);


  // Sanitize phone number to E.164 format (remove all non-digit characters except leading +)
  const sanitizePhoneNumber = (phone) => {
    if (!phone) return '';
    // Keep the leading + and remove all non-digit characters
    return '+' + phone.replace(/\D/g, '');
  };

  async function handleSignUp() {
    if (!newUser.email) {
      setError({ message: t("Please enter your email address.") });
      return;
    }
    if (!newUser.firstName) {
      setError({ message: t("Please enter your first name.") });
      return;
    }
    if (!newUser.lastName) {
      setError({ message: t("Please enter your last name.") });
      return;
    }
    if (!newUser.password) {
      setError({ message: t("Please enter your password.") });
      return;
    }
    if (!newUser.passwordConfirm) {
      setError({ message: t("Please confirm your password.") });
      return;
    }
    if (newUser.password !== newUser.passwordConfirm) {
      setError({ message: t("Passwords do not match.") });
      return;
    }
    try {
      const { isSignUpComplete, userId, nextStep } = await signUp({
        username: newUser.email,
        password: newUser.password,
        options: {
          userAttributes: {
            email: newUser.email,
            phone_number: sanitizePhoneNumber(newUser.phone),
            given_name: newUser.firstName,
            family_name: newUser.lastName,
            middle_name: newUser.middleName
          },
          // optional
          autoSignIn: true // or SignInOptions e.g { authFlowType: "USER_SRP_AUTH" }
        }
      });

      console.log(userId);
      hotjar.identify(userId, { email: newUser.email })
      hotjar.event("Signed up");
      console.log(isSignUpComplete);
      console.log(nextStep);
      if (!isSignUpComplete && nextStep.signUpStep === "CONFIRM_SIGN_UP") {
        setIsStepConfirm(true);
        setEmailForConfirmation(newUser.email);
      }
    } catch (error) {
      console.log('error signing up:', error);
      captureError(error, {
        feature: 'authentication',
        operation: 'sign-up',
        context: { email: newUser.email },
        tags: { component: 'SignUp' }
      });
      hotjar.event("Error signing up");
      if (newUser.email) hotjar.identify(newUser.email);
      setError(error);
    }
  }

  async function handleSignUpConfirmation() {
    setIsLoading(true);
    try {
      const { isSignUpComplete, nextStep } = await confirmSignUp({
        username: newUser.email,
        confirmationCode: newUser.confirmationCode
      });
      await signIn({ username: newUser.email, password: password || newUser.password });
      hotjar.event("Confirmed sign up");
    } catch (error) {
      console.log('error confirming sign up', error);
      captureError(error, {
        feature: 'authentication',
        operation: 'confirm-sign-up',
        context: { email: newUser.email },
        tags: { component: 'SignUp' }
      });
      hotjar.event("Login failed")
      if (newUser.email) hotjar.identify(newUser.email);
      setError(error);
      if (error.message === "Incorrect username or password.") {
        setAuthRoute("signIn");
      }
    }
    setIsLoading(false);
  }




  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await handleSignUp();
    } catch (error) {
      console.error(error);
      captureError(error, {
        feature: 'authentication',
        operation: 'submit-sign-up',
        context: { email: newUser.email },
        tags: { component: 'SignUp' }
      });
    }
  };

  // Detect if user is in booking process
  const [bookingProcess, setBookingProcess] = useState(false);
  const [bookingProcessData, setBookingProcessData] = useState({});

  useEffect(() => {
    let params = new URL(window.location.href).searchParams;
    if (window.location.href.includes("checkout")) {
      setBookingProcess(true);
      setBookingProcessData({
        productName: params.get('productName'),

      });
    }
    if (window.location.href.includes("book-ticket")) {
      setBookingProcess(true);
      loadEvent(params.get('eventId'), params.get('ticketType'));
    }
  }, []);


  return (
    <>

      <MDBRow className="d-flex align-items-center m-0" style={{
        minHeight: "100vh",
        paddingBottom: "5rem",
        backgroundImage: "url(https://iec-cp-public.s3.eu-central-1.amazonaws.com/2_2500x488px_lounges_ka_2024.jpg)",
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
      }}>
        <MDBCol size="12" className="d-flex align-items-center justify-content-center">
          <MDBCard className="col-12 col-md-8 col-xl-6 col-xxl-4">
            <MDBCardBody className={bookingProcess ? "py-2 py-md-3 px-3 px-md-4" : ""}>

              {bookingProcess &&
                <div className="text-center mb-1">
                  <MDBCardTitle className="mb-1" style={{ fontSize: "1rem" }}>{t("Your booking")}:</MDBCardTitle>
                  <div className='d-flex align-items-center justify-content-center'>
                    <div className='d-flex align-items-center text-start'>
                      <MDBIcon style={{ fontSize: "18px" }} fas icon="shopping-cart" />
                      <div className='ms-2'>
                        <p className='fw-bold mb-0' style={{ fontSize: "0.9rem" }}>
                          {bookingProcessData.productName
                            ? t(bookingProcessData.productName)
                            : <span className="placeholder-glow"><span className="placeholder" style={{ width: "120px", display: "inline-block" }}></span></span>
                          }
                        </p>
                        <p className='text-muted mb-0' style={{ fontSize: "0.85rem" }}>
                          {bookingProcessData.productDescription
                            ? t(bookingProcessData.productDescription)
                            : <span className="placeholder-glow"><span className="placeholder" style={{ width: "60px", display: "inline-block" }}></span></span>
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="alert alert-info py-1 mx-auto mt-1 mb-0" style={{ width: "fit-content", fontSize: "0.85rem" }}>
                    <h6 className="mb-0" style={{ fontSize: "0.85rem" }}>{t("Please sign in to continue")}</h6>
                  </div>
                  <hr className="my-2" />
                </div>
              }

              <MDBTabs pills justify className='mb-2'>
                <MDBTabsItem>
                  <MDBTabsLink
                    onClick={() => setAuthRoute("signIn")}
                  >
                    {t("Login")}
                  </MDBTabsLink>
                </MDBTabsItem>
                <MDBTabsItem>
                  <MDBTabsLink
                    onClick={() => setAuthRoute("signUp")}
                    active
                  >
                    {t("Register")}
                  </MDBTabsLink>
                </MDBTabsItem>
              </MDBTabs>

              <MDBTabsContent>
                <MDBTabsPane open={true}>


                  {!isStepConfirm ?
                    <>
                      <h1 className="mb-2">{t("Register")}</h1>
                      <form onSubmit={handleSubmit}>
                        <MDBInput className='mb-3' type='email' id='email' label={t("Email address")} value={newUser.email} onChange={handleChangeInput} />
                        {emailAlreadyExists ?
                          <div className="alert alert-danger">
                            {t("Email already exists, please try to log in or use another email address.")}
                            <br></br>
                            <MDBBtn color="primary" onClick={() => setAuthRoute("signIn")}>{t("Login")}</MDBBtn>
                          </div>
                          : <>
                            <MDBRow>
                              <MDBCol size="12" md="6">
                                <MDBInput className='mb-3' type='text' id='firstName' label={t("First Name")} value={newUser.firstName} onChange={handleChangeInput} />
                              </MDBCol>
                              <MDBCol size="12" md="6">
                                <MDBInput className='mb-3' type='text' id='lastName' label={t("Last Name")} value={newUser.lastName} onChange={handleChangeInput} />
                              </MDBCol>
                            </MDBRow>
                            <PhoneInput className="mb-3" country={'de'} enableSearch value={newUser.phone} onChange={e => setNewUser(old => ({ ...old, phone: "+" + e }))} />

                            <MDBInput className='mb-1' type='password' id='password' label={t("Password")} value={newUser.password} onChange={handleChangeInput} />
                            <small className="text-muted d-block mb-2">{t("Minimum 8 characters")}</small>
                            <MDBInput className='mb-3' type='password' id='passwordConfirm' label={t("Confirm Password")} value={newUser.passwordConfirm} onChange={handleChangeInput} />
                          </>
                        }
                        <MDBBtn type='submit' className='mb-3' block>{t("Register")} </MDBBtn>
                      </form>
                      {error && <div className="alert alert-danger">{t(error.message)}</div>}
                    </>
                    : <div>
                      <h2>{t("Registration successful")}</h2>
                      <h4>{t("Please check your email for a confirmation code.")}</h4>
                      <p>{t("If you haven't received an email, click here or contact our support.")} <span className="text-primary" style={{ cursor: "pointer" }} onClick={() => {
                        const username = newUser.email || email;
                        if (!username) { setError({ message: "Please enter your email address to resend the code." }); return; }
                        resendSignUpCode({ username });
                      }}>{t("Resend")}</span></p>

                      {!emailForConfirmation && <MDBInput className='mb-4' type='email' id='email' label={t("Email address")} value={newUser.email} onChange={handleChangeInput} />}
                      <MDBInput className='mb-4' type='text' id='confirmationCode' label={t("Confirmation code")} value={newUser.confirmationCode} onChange={handleChangeInput} />

                      {
                        isLoading ? <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">{t("Loading...")}</span>
                        </div> :
                          <MDBBtn onClick={handleSignUpConfirmation} disabled={newUser.confirmationCode.length < 4} className='mb-4' block>{t("Confirm")} </MDBBtn>
                      }


                      {error && <div className="alert alert-danger text-center">{t(error.message)}</div>}
                      {resendInvitationStatus === "sent" && <div className="alert alert-success text-center">{t("Invitation email sent")}</div>}
                    </div>
                  }
                </MDBTabsPane>
              </MDBTabsContent>
            </MDBCardBody>
          </MDBCard>
        </MDBCol>

      </MDBRow>
      <LangSelect style={{ top: "0.5rem", right: "0.5rem", position: "absolute" }} className="langSelect--login" />
      <Footer currentIndustry={currentIndustry} />
    </>
  );
};

export default SignUp;